import { OverlayModule } from '@angular/cdk/overlay';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import dayjs, { Dayjs } from 'dayjs';
import { CalendarComponent } from './calendar.component';
import { PRESET_RANGES, PresetRange, SOURCES_OPTIONS, STATUS_OPTIONS } from './date-range-picker.constants';
import { SourceFilter, StatusFilter, TrackerPositionFilters } from './date-range-picker.types';
import { TimeFieldComponent } from './time-field.component';
import { IntegratorMultiSelectComponent } from '../integrator-multi-select.component';
import { IntegratorSelectComponent } from '../integrator-select.component';
import { LanguageService } from '../services/language.service';

@Component({
  selector: 'lib-date-range-picker',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    OverlayModule,
    TranslocoModule,
    CalendarComponent,
    IntegratorMultiSelectComponent,
    IntegratorSelectComponent,
    TimeFieldComponent,
  ],
  template: `
    <div class="flex flex-wrap items-center gap-2">
      <div class="relative">
        <button
          type="button"
          cdkOverlayOrigin
          #trigger="cdkOverlayOrigin"
          (click)="handleOpen()"
          [attr.aria-expanded]="isOpen()"
          class="flex h-8 w-full items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-3 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 sm:w-auto sm:justify-start dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 dark:hover:bg-slate-600">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="size-3.5 shrink-0">
            <rect width="18" height="18" x="3" y="4" rx="2" ry="2" />
            <line x1="16" x2="16" y1="2" y2="6" />
            <line x1="8" x2="8" y1="2" y2="6" />
            <line x1="3" x2="21" y1="10" y2="10" />
          </svg>
          <span class="truncate">{{ triggerLabel() }}</span>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            class="size-3.5 shrink-0 opacity-60">
            <path stroke-linecap="round" stroke-linejoin="round" d="m6 9 6 6 6-6" />
          </svg>
        </button>

        <ng-template
          cdkConnectedOverlay
          [cdkConnectedOverlayOrigin]="trigger"
          [cdkConnectedOverlayOpen]="isOpen()"
          [cdkConnectedOverlayHasBackdrop]="true"
          [cdkConnectedOverlayBackdropClass]="'cdk-overlay-transparent-backdrop'"
          [cdkConnectedOverlayFlexibleDimensions]="true"
          [cdkConnectedOverlayGrowAfterOpen]="true"
          [cdkConnectedOverlayPush]="true"
          [cdkConnectedOverlayViewportMargin]="8"
          [cdkConnectedOverlayPositions]="overlayPositions"
          (backdropClick)="isOpen.set(false)"
          (detach)="isOpen.set(false)">
          <div
            class="flex max-h-[90vh] w-[95vw] flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl sm:w-[720px] dark:border-slate-700 dark:bg-slate-800"
            style="max-width: 720px; min-width: 280px"
            (keydown.escape)="isOpen.set(false)">
            <div class="flex min-h-0 flex-1 flex-col overflow-auto sm:flex-row">
              <!-- Presets -->
              <div
                class="border-b border-gray-200 p-2 sm:w-44 sm:shrink-0 sm:border-b-0 sm:border-r dark:border-slate-700">
                <h3
                  class="mb-1.5 px-2 text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-slate-500">
                  {{ 'dateRangePicker.presetsTitle' | transloco }}
                </h3>
                <div class="flex flex-row flex-wrap gap-1 sm:flex-col sm:flex-nowrap">
                  @for (preset of presetRanges; track preset.labelKey) {
                    <button type="button" (click)="handlePreset(preset)" [class]="presetClass(preset)">
                      {{ 'dateRangePicker.presets.' + preset.labelKey | transloco }}
                    </button>
                  }
                </div>
              </div>

              <!-- Custom range -->
              <div class="min-w-0 flex-1 p-3">
                <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div class="flex flex-col gap-2">
                    <p
                      class="text-center text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-slate-400">
                      {{ 'dateRangePicker.start' | transloco }}
                    </p>
                    <lib-calendar
                      [month]="startMonth()"
                      [selectedDate]="tempFrom()"
                      [maxDate]="getMaxDate(tempTo())"
                      [rangeFrom]="tempFrom()"
                      [rangeTo]="tempTo()"
                      (monthChange)="startMonth.set($event)"
                      (dateSelect)="handleFromDateChange($event)" />
                    <lib-time-field [value]="tempFrom()" (valueChange)="tempFrom.set($event)" />
                  </div>
                  <div class="flex flex-col gap-2">
                    <p
                      class="text-center text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-slate-400">
                      {{ 'dateRangePicker.end' | transloco }}
                    </p>
                    <lib-calendar
                      [month]="endMonth()"
                      [selectedDate]="tempTo()"
                      [minDate]="tempFrom()"
                      [maxDate]="now()"
                      [rangeFrom]="tempFrom()"
                      [rangeTo]="tempTo()"
                      (monthChange)="endMonth.set($event)"
                      (dateSelect)="handleToDateChange($event)" />
                    <lib-time-field [value]="tempTo()" (valueChange)="tempTo.set($event)" />
                  </div>
                </div>
              </div>
            </div>

            <!-- Footer: live readout of the draft range + its duration, then the actions. -->
            <div
              class="flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 bg-gray-50 px-3 py-2.5 dark:border-slate-700 dark:bg-slate-900/50">
              <div class="min-w-0 text-xs text-gray-600 dark:text-slate-400">
                <span class="font-mono font-medium text-gray-900 dark:text-slate-100">{{ draftRangeText() }}</span>
                <span class="ml-2 rounded bg-gray-200 px-1.5 py-0.5 font-medium dark:bg-slate-700">
                  {{ draftDuration() }}
                </span>
              </div>
              <div class="flex gap-2">
                <button
                  type="button"
                  (click)="isOpen.set(false)"
                  class="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700">
                  {{ 'dateRangePicker.cancel' | transloco }}
                </button>
                <button
                  type="button"
                  (click)="handleApplyCustom()"
                  class="bg-primary-500 hover:bg-primary-600 rounded-md px-3 py-1.5 text-sm font-semibold text-white shadow-sm transition-colors">
                  {{ 'dateRangePicker.apply' | transloco }}
                </button>
              </div>
            </div>
          </div>
        </ng-template>
      </div>

      @if (!hideStatusFilter()) {
        <lib-integrator-select
          class="w-full sm:w-44"
          width="100%"
          size="sm"
          accent="primary"
          [options]="statusOptions()"
          [ngModel]="statusFilter()"
          (ngModelChange)="statusFilter.set($event)" />

        <lib-integrator-multi-select
          class="w-full sm:w-44"
          width="100%"
          size="sm"
          accent="primary"
          itemNoun="sources"
          [placeholder]="'dateRangePicker.allSources' | transloco"
          [options]="sourceOptions"
          [ngModel]="sourceFilter()"
          (ngModelChange)="sourceFilter.set($event)" />
      }

      <!-- Apply — re-emits {@link filtersApplied} with the *current* date
           range plus whatever status/source the operator just picked. We
           call {@link reapplyCurrent} (not {@link handleApplyCustom}) so we
           don't accidentally overwrite the actual date range with the
           popover's stale {@code tempFrom}/{@code tempTo} draft values. -->
      <button
        type="button"
        class="inline-flex h-8 items-center gap-1.5 rounded-md px-3 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50"
        [class]="applyClass()"
        [title]="'dateRangePicker.apply' | transloco"
        (click)="reapplyCurrent()">
        <svg class="size-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2.5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
        </svg>
        {{ 'dateRangePicker.apply' | transloco }}
      </button>
    </div>
  `,
})
export class DateRangePickerComponent {
  readonly initialFilters = input.required<TrackerPositionFilters>();
  readonly hideStatusFilter = input<boolean>(false);
  /**
   * Colour of the Apply button. 'primary' (pink) for tracker pages and the position history,
   * 'warmYellow' on gateway pages (their identity colour), 'neutral' where no accent is wanted.
   */
  readonly accent = input<'primary' | 'warmYellow' | 'neutral'>('primary');
  protected readonly applyClass = computed(() => {
    switch (this.accent()) {
      case 'warmYellow':
        return 'bg-warmYellow-500 text-gray-900 hover:bg-warmYellow-400';
      case 'neutral':
        return 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 dark:hover:bg-slate-600';
      default:
        return 'bg-primary-500 text-white hover:bg-primary-600';
    }
  });
  readonly filtersApplied = output<TrackerPositionFilters>();

  readonly overlayPositions = [
    {
      originX: 'start' as const,
      originY: 'bottom' as const,
      overlayX: 'start' as const,
      overlayY: 'top' as const,
      offsetY: 8,
    },
    {
      originX: 'start' as const,
      originY: 'top' as const,
      overlayX: 'start' as const,
      overlayY: 'bottom' as const,
      offsetY: -8,
    },
    {
      originX: 'end' as const,
      originY: 'bottom' as const,
      overlayX: 'end' as const,
      overlayY: 'top' as const,
      offsetY: 8,
    },
    {
      originX: 'end' as const,
      originY: 'top' as const,
      overlayX: 'end' as const,
      overlayY: 'bottom' as const,
      offsetY: -8,
    },
  ];

  readonly isOpen = signal(false);
  readonly from = signal<Dayjs>(dayjs().subtract(1, 'hour'));
  readonly to = signal<Dayjs>(dayjs());
  readonly statusFilter = signal<StatusFilter>(StatusFilter.BOTH);
  readonly sourceFilter = signal<SourceFilter[]>([SourceFilter.ALL]);

  readonly tempFrom = signal<Dayjs>(dayjs().subtract(1, 'hour'));
  readonly tempTo = signal<Dayjs>(dayjs());

  readonly startMonth = signal<Dayjs>(dayjs().startOf('month'));
  readonly endMonth = signal<Dayjs>(dayjs().startOf('month'));

  /** Which preset produced the committed range, if any — drives the trigger
   * label and the active highlight in the rail. Cleared by a custom apply. */
  readonly activePresetKey = signal<string | null>(null);

  protected readonly presetRanges = PRESET_RANGES;
  protected readonly sourceOptions = SOURCES_OPTIONS;

  #transloco = inject(TranslocoService);
  #lang = inject(LanguageService);

  protected readonly statusOptions = computed(() => {
    this.#lang.active();
    return STATUS_OPTIONS.map(o => ({
      label: this.#transloco.translate('dateRangePicker.status.' + o.labelKey),
      value: o.value,
    }));
  });

  /** `dayjs()` is not reactive — this only needs to be fresh each time the
   * popover opens, which {@link handleOpen} guarantees by re-seeding the temps. */
  protected now(): Dayjs {
    return dayjs();
  }

  /** Named preset when one is active, explicit dates otherwise — "Last hour"
   * reads far faster than "16/07/2026 08:39 - 16/07/2026 09:39". */
  protected readonly triggerLabel = computed(() => {
    this.#lang.active();
    const key = this.activePresetKey();
    if (key) return this.#transloco.translate('dateRangePicker.presets.' + key);
    return `${this.from().format('DD/MM/YYYY HH:mm')} - ${this.to().format('DD/MM/YYYY HH:mm')}`;
  });

  protected readonly draftRangeText = computed(
    () => `${this.tempFrom().format('DD/MM/YYYY HH:mm')} → ${this.tempTo().format('DD/MM/YYYY HH:mm')}`
  );

  /** Compact human duration of the draft — "2 j 4 h" / "2 d 4 h", "35 min". */
  protected readonly draftDuration = computed(() => {
    this.#lang.active();
    const unit = (k: string) => this.#transloco.translate('dateRangePicker.units.' + k);
    const mins = Math.max(0, this.tempTo().diff(this.tempFrom(), 'minute'));
    const d = Math.floor(mins / 1440);
    const h = Math.floor((mins % 1440) / 60);
    const m = mins % 60;
    const parts: string[] = [];
    if (d) parts.push(`${d} ${unit('day')}`);
    if (h) parts.push(`${h} ${unit('hour')}`);
    if (m || !parts.length) parts.push(`${m} ${unit('minute')}`);
    return parts.join(' ');
  });

  protected presetClass(preset: PresetRange): string {
    const base = 'w-full rounded-md px-2.5 py-1.5 text-left text-[13px] transition-colors';
    return this.activePresetKey() === preset.labelKey
      ? `${base} bg-primary-500 font-semibold text-white`
      : `${base} text-gray-700 hover:bg-gray-100 dark:text-slate-300 dark:hover:bg-slate-700`;
  }

  getMaxDate(endDate: Dayjs): Dayjs {
    const today = dayjs();
    return endDate.isAfter(today) ? today : endDate;
  }

  constructor() {
    effect(
      () => {
        const filters = this.initialFilters();
        if (filters.from) this.from.set(dayjs(filters.from));
        if (filters.to) this.to.set(dayjs(filters.to));
        if (filters.statusFilter) this.statusFilter.set(filters.statusFilter);
      },
      { allowSignalWrites: true }
    );
  }

  handleOpen(): void {
    this.tempFrom.set(this.from());
    this.tempTo.set(this.to());
    this.startMonth.set(this.from().startOf('month'));
    this.endMonth.set(this.to().startOf('month'));
    this.isOpen.set(true);
  }

  handlePreset(preset: PresetRange): void {
    const [newFrom, newTo] = preset.range();
    const today = dayjs();
    const clampedTo = newTo.isAfter(today) ? today : newTo;
    const finalFrom = newFrom.second(0).millisecond(0);
    const finalTo = clampedTo.second(59).millisecond(999);
    this.from.set(finalFrom);
    this.to.set(finalTo);
    this.activePresetKey.set(preset.labelKey);
    this.#emit(finalFrom, finalTo);
    this.isOpen.set(false);
  }

  /**
   * Re-emit the current filter state without touching the date range. Used
   * by the outer Apply button — the operator changed {@code statusFilter} or
   * {@code sourceFilter} after the popover closed and wants to push the new
   * values to the parent. Reads {@code from}/{@code to} (the committed
   * range), NOT {@code tempFrom}/{@code tempTo} (the popover's draft).
   */
  reapplyCurrent(): void {
    this.#emit(this.from(), this.to());
  }

  handleApplyCustom(): void {
    const today = dayjs();
    const clampedFrom = this.tempFrom().isAfter(today) ? today : this.tempFrom();
    const clampedTo = this.tempTo().isAfter(today) ? today : this.tempTo();
    const finalFrom = clampedFrom.second(0).millisecond(0);
    const finalTo = clampedTo.second(59).millisecond(999);

    this.from.set(finalFrom);
    this.to.set(finalTo);
    this.activePresetKey.set(null);
    this.#emit(finalFrom, finalTo);
    this.isOpen.set(false);
  }

  handleFromDateChange(date: Dayjs): void {
    const newFrom = date.hour(this.tempFrom().hour()).minute(this.tempFrom().minute()).second(0).millisecond(0);
    this.tempFrom.set(newFrom);
    if (newFrom.isAfter(this.tempTo())) this.tempTo.set(newFrom);
  }

  handleToDateChange(date: Dayjs): void {
    const newTo = date.hour(this.tempTo().hour()).minute(this.tempTo().minute()).second(59).millisecond(999);
    this.tempTo.set(newTo);
    if (newTo.isBefore(this.tempFrom())) this.tempFrom.set(newTo);
  }

  #emit(from: Dayjs, to: Dayjs): void {
    this.filtersApplied.emit({
      from: from.toDate(),
      to: to.toDate(),
      statusFilter: this.statusFilter(),
      sourceFilter: this.sourceFilter(),
    });
  }
}
